import React, { useState, useEffect } from "react";
import { Plus, Minus } from "lucide-react";

/**
 * Editorial FAQ section with FAQPage JSON-LD for rich-snippet eligibility.
 * Props:
 *  - eyebrow: small uppercase label above the heading
 *  - title: React node for the h2 heading
 *  - intro: optional intro paragraph (string or node)
 *  - items: array of { q: string, a: string | node }
 *  - theme: 'light' (default cream) | 'dark' (deep teal)
 *  - testId: data-testid prefix
 */
export default function FaqSection({
  eyebrow = "Frequently Asked",
  title,
  intro,
  items = [],
  theme = "light",
  testId = "faq",
}) {
  const [open, setOpen] = useState(0);
  const dark = theme === "dark";

  // Inject FAQPage JSON-LD for the rich snippet
  useEffect(() => {
    const id = `faq-jsonld-${testId}`;
    let script = document.getElementById(id);
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: {
          "@type": "Answer",
          // strip JSX for the JSON; if a node was passed, use a plain-text fallback
          text:
            typeof it.a === "string"
              ? it.a
              : it.aText || it.q,
        },
      })),
    };
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
    return () => {
      const el = document.getElementById(id);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, [items, testId]);

  return (
    <section
      data-testid={`${testId}-section`}
      className={
        dark
          ? "bg-[#0f2420] text-white border-t border-[#c99658]/15"
          : "bg-cream text-[#0f2420] border-t border-[#c99658]/15"
      }
    >
      <div className="container-vlx py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <span
              className={
                dark
                  ? "text-[10.5px] tracking-[0.5em] uppercase font-raleway text-[#e2b880]"
                  : "text-[10.5px] tracking-[0.5em] uppercase font-raleway text-[#7a5b2f]"
              }
            >
              {eyebrow}
            </span>
            <div className="mt-4 rule-gold" />
            <h2
              className={`heading-lg mt-8 text-[30px] md:text-[44px] leading-[1.1] ${
                dark ? "text-white" : "text-[#0f2420]"
              }`}
            >
              {title}
            </h2>
            {intro && (
              <p
                className={`mt-6 text-[14.5px] leading-[1.9] font-nunito max-w-md ${
                  dark ? "text-white/70" : "text-neutral-700"
                }`}
              >
                {intro}
              </p>
            )}
          </div>

          <ul
            className={`md:col-span-7 ${
              dark
                ? "divide-y divide-[#c99658]/20"
                : "divide-y divide-[#c99658]/30"
            }`}
          >
            {items.map((it, i) => {
              const isOpen = open === i;
              return (
                <li key={it.q} data-testid={`${testId}-item-${i}`}>
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className={`w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left group ${
                      dark ? "hover:text-[#e2b880]" : "hover:text-[#7a5b2f]"
                    } transition-colors`}
                  >
                    <span
                      className={`font-cormorant text-[20px] md:text-[24px] leading-[1.3] ${
                        dark ? "text-white" : "text-[#0f2420]"
                      }`}
                    >
                      {it.q}
                    </span>
                    <span
                      className={`flex-shrink-0 mt-1 w-8 h-8 inline-flex items-center justify-center rounded-full ${
                        dark
                          ? "bg-[#c99658]/15 text-[#e2b880]"
                          : "bg-[#0f2420]/5 text-[#7a5b2f]"
                      } group-hover:bg-[#c99658] group-hover:text-[#0f2420] transition-colors`}
                    >
                      {isOpen ? (
                        <Minus className="w-4 h-4" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      className={`pb-7 pr-12 text-[14.5px] leading-[1.95] font-nunito ${
                        dark ? "text-white/75" : "text-neutral-700"
                      }`}
                    >
                      {it.a}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
